
import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './SplashScreen.css';

/**
 * SplashScreen Component
 * 
 * Displays a static image splash screen on app launch with:
 * - Static logo image (splash0125.png)
 * - Click-anywhere to skip to login
 * - Automatic navigation to login after 3 seconds
 * 
 * @component
 */
const SplashScreen = ({ onComplete }) => {
  const navigate = useNavigate();
  const navigationTimeoutRef = useRef(null);

  /**
   * Set up automatic navigation after 3 seconds
   */
  useEffect(() => {
    // Navigate to login after 3 seconds
    navigationTimeoutRef.current = setTimeout(() => {
      onComplete();
      navigate('/login');
    }, 3000);

    // Cleanup on unmount
    return () => {
      if (navigationTimeoutRef.current) {
        clearTimeout(navigationTimeoutRef.current);
      }
    };
  }, [navigate, onComplete]);

  /**
   * Handles click event to skip animation
   * Navigates immediately to login page
   */
  const handleClick = () => {
    // Clear pending timeout
    if (navigationTimeoutRef.current) {
      clearTimeout(navigationTimeoutRef.current);
    }
    
    // Update parent state first, then navigate
    onComplete();
    navigate('/login');
  };

  return (
    <div 
      className="splash-screen-container" 
      onClick={handleClick}
      role="button"
      aria-label="Click to skip splash screen"
      tabIndex={0}
      onKeyPress={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          handleClick();
        }
      }}
    >
      {/* Static splash image */}
      <img
        src="/splash0125.png"
        alt="On-Screen Logo"
        className="splash-logo-image"
        aria-label="On-Screen splash logo"
      />

      {/* Skip hint (fades in after 0.5 second) */}
      <div className="skip-hint">
        Click anywhere to continue
      </div>
    </div>
  );
};

export default SplashScreen;